import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Package, DollarSign, ShoppingCart, Users, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { slug: "", name: "", collection: "medication", price: 0, image: "", short_description: "", description: "", rating: 5, reviews_count: 0, badge: "", featured: false, in_stock: true, type: "product" };
const COLLECTIONS = ["medication", "hydration", "mobility", "appetite", "litter", "bundles", "guides"];

export default function Admin() {
  const { user, ready } = useAuth();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(false);

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
  };

  useEffect(() => { if (user && user.role === "admin") loadAll(); }, [user]);

  if (!ready) return <div className="text-center py-32 text-gray-400">Loading…</div>;
  if (user === false) return <Navigate to="/login" state={{ from: "/admin" }} replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const openNew = () => { setForm(EMPTY); setEditing(false); setOpen(true); };
  const openEdit = (p) => { setForm({ ...p }); setEditing(true); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, price: parseFloat(form.price), rating: parseFloat(form.rating), reviews_count: parseInt(form.reviews_count) || 0 };
      if (editing) await api.put(`/admin/products/${form.slug}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Product updated" : "Product created");
      setOpen(false);
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const del = async (slug) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${slug}`);
    toast.success("Product deleted");
    loadAll();
  };

  const statCards = [
    { label: "Revenue", value: `$${stats?.revenue?.toFixed(2) || "0.00"}`, icon: DollarSign },
    { label: "Orders", value: stats?.total_orders ?? 0, icon: ShoppingCart },
    { label: "Products", value: stats?.total_products ?? 0, icon: Package },
    { label: "Customers", value: stats?.total_customers ?? 0, icon: Users },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="font-display font-black text-4xl tracking-tight mb-8">Admin Dashboard</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {statCards.map((s) => (
          <div key={s.label} className="bg-white border border-gray-100 rounded-2xl p-6" data-testid={`stat-${s.label.toLowerCase()}`}>
            <div className="w-10 h-10 rounded-xl bg-[#FF5C28]/10 flex items-center justify-center mb-3">
              <s.icon className="w-5 h-5 text-[#FF5C28]" />
            </div>
            <p className="font-display font-black text-2xl">{s.value}</p>
            <p className="text-sm text-gray-500">{s.label}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="products">
        <TabsList className="rounded-full bg-[#F8F9FA] p-1 mb-6">
          <TabsTrigger value="products" data-testid="tab-products" className="rounded-full">Products</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-full">Orders</TabsTrigger>
        </TabsList>

        <TabsContent value="products">
          <div className="flex justify-end mb-4">
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button onClick={openNew} data-testid="add-product-btn" className="inline-flex items-center gap-2 rounded-full bg-[#FF5C28] text-white font-bold px-5 py-2.5 hover:bg-[#E04D1E] transition-colors">
                  <Plus className="w-4 h-4" /> Add Product
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Product</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  {[
                    ["slug", "Slug (unique)"], ["name", "Name"], ["image", "Image URL"],
                    ["short_description", "Short description"], ["badge", "Badge"],
                  ].map(([k, label]) => (
                    <input key={k} data-testid={`pf-${k}`} placeholder={label} value={form[k]} disabled={editing && k === "slug"}
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      className="w-full bg-[#F8F9FA] border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#FF5C28] disabled:opacity-50" />
                  ))}
                  <textarea data-testid="pf-description" placeholder="Full description" value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="w-full bg-[#F8F9FA] border border-gray-200 rounded-xl px-4 py-2.5 text-sm h-20 focus:outline-none focus:border-[#FF5C28]" />
                  <div className="grid grid-cols-2 gap-3">
                    <input data-testid="pf-price" type="number" step="0.01" placeholder="Price" value={form.price}
                      onChange={(e) => setForm({ ...form, price: e.target.value })}
                      className="bg-[#F8F9FA] border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#FF5C28]" />
                    <select data-testid="pf-collection" value={form.collection} onChange={(e) => setForm({ ...form, collection: e.target.value })}
                      className="bg-[#F8F9FA] border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#FF5C28]">
                      {COLLECTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <input data-testid="pf-rating" type="number" step="0.1" placeholder="Rating" value={form.rating}
                      onChange={(e) => setForm({ ...form, rating: e.target.value })}
                      className="bg-[#F8F9FA] border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#FF5C28]" />
                    <input data-testid="pf-reviews" type="number" placeholder="Reviews count" value={form.reviews_count}
                      onChange={(e) => setForm({ ...form, reviews_count: e.target.value })}
                      className="bg-[#F8F9FA] border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#FF5C28]" />
                  </div>
                  <label className="flex items-center gap-2 text-sm font-medium">
                    <input type="checkbox" data-testid="pf-featured" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured product
                  </label>
                </div>
                <DialogFooter>
                  <button onClick={save} data-testid="save-product-btn" className="rounded-full bg-[#FF5C28] text-white font-bold px-6 py-2.5 hover:bg-[#E04D1E] transition-colors">Save</button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden" data-testid="admin-products-table">
            {products.map((p) => (
              <div key={p.slug} className="flex items-center gap-4 p-4 border-b border-gray-50 last:border-0">
                <img src={p.image} alt={p.name} className="w-12 h-12 object-contain bg-[#F8F9FA] rounded-lg" />
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate">{p.name}</p>
                  <p className="text-xs text-gray-400">{p.collection} · ${p.price.toFixed(2)}</p>
                </div>
                <button onClick={() => openEdit(p)} data-testid={`edit-${p.slug}`} className="p-2 text-gray-400 hover:text-[#FF5C28]"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => del(p.slug)} data-testid={`delete-${p.slug}`} className="p-2 text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="orders">
          <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden" data-testid="admin-orders-table">
            {orders.length === 0 ? (
              <p className="p-8 text-center text-gray-400">No orders yet.</p>
            ) : orders.map((o) => (
              <div key={o.session_id} className="flex items-center justify-between p-4 border-b border-gray-50 last:border-0">
                <div className="min-w-0">
                  <p className="font-bold truncate">{o.user_email}</p>
                  <p className="text-xs text-gray-400 truncate">{o.line_summary?.join(", ")}</p>
                  <p className="text-xs text-gray-400">{new Date(o.created_at).toLocaleString()}</p>
                </div>
                <div className="text-right">
                  <p className="font-display font-black">${o.amount?.toFixed(2)}</p>
                  <span className="text-xs font-bold text-emerald-600">{o.status}</span>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
